<template>
  <div class="wallet_data_page">
    <!-- WALLET HERO SUMMARY CARD (ALWAYS VISIBLE) -->
    <div class="wallet_hero_card mb-4" v-if="userInfo">
      <div class="hero_ambient_glow"></div>
      <div class="card_inner">
        <div class="info_col">
          <div class="badge_pill">
            <i class="fal fa-wallet"></i>
            <span>محفظة السائق</span>
          </div>
          <h4 class="hero_title">رصيد المحفظة الحالي</h4>
          <div class="balance_amount_row">
            <span class="currency_sym">ر.س</span>
            <span class="amount_num">{{ userInfo.wallet || 0 }}</span>
          </div>
        </div>

        <div class="wallet_icon_col">
          <div class="glowing_icon_box">
            <i class="fal fa-wallet"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- MAIN CONTENT SECTION -->
    <div class="wallet_content_card">
      <div
        class="card_header_bar d-flex align-items-center justify-content-between px-4 pt-4 pb-3"
      >
        <div class="header_title">
          <i class="fal fa-history text_primary"></i>
          <h5 class="m-0 font-weight-bold">سجل المعاملات المالية</h5>
        </div>
        <span class="count_badge" v-if="transactions && transactions.length">
          {{ transactions.length }} معاملة
        </span>
      </div>

      <!-- LOADER STATE -->
      <div
        class="py-5 text-center"
        v-if="data.loading && (!transactions || !transactions.length)"
      >
        <v-progress-circular
          indeterminate
          color="purple darken-2"
          size="50"
          width="4"
        ></v-progress-circular>
        <p class="mt-3 text-muted font-weight-bold">
          جاري تحميل المعاملات المالية...
        </p>
      </div>

      <!-- EMPTY STATE (WHEN TRANSACTIONS ARE EMPTY) -->
      <div
        class="empty_state_container py-5 px-4 text-center"
        v-else-if="!data.loading && transactions && transactions.length === 0"
      >
        <div class="empty_icon_wrapper mx-auto mb-4">
          <div class="empty_icon_box">
            <i class="fal fa-receipt"></i>
          </div>
          <div class="pulse_ring"></div>
        </div>
        <h4 class="empty_title font-weight-bold mb-2">
          لا توجد معاملات مالية مسجلة
        </h4>
        <p
          class="empty_subtitle text-muted mx-auto mb-4"
          style="max-width: 480px"
        >
          لم يتم إجراء أي عمليات إيداع، سحب، أو تحويل رصيد على محفظة هذا السائق
          حتى الآن.
        </p>
      </div>

      <!-- TRANSACTIONS TABLE SECTION -->
      <div v-else-if="transactions && transactions.length">
        <v-data-table
          class="custom_creative_table"
          :headers="headers"
          :items="transactions"
          :loading="data.loading"
          :loading-text="$t('table.loadingData')"
          item-key="id"
          :items-per-page="paginations.items_per_page"
          hide-default-footer
        >
          <template v-slot:[`item.index`]="{ index }">
            <span class="index_badge">#{{ index + 1 }}</span>
          </template>

          <template v-slot:[`item.title_trans`]="{ item }">
            <span class="font-weight-bold text-dark">{{
              item.title_trans || item.title || "-"
            }}</span>
          </template>

          <template v-slot:[`item.amount`]="{ item }">
            <span
              class="amount_badge"
              :class="Number(item.amount) >= 0 ? 'income' : 'expense'"
            >
              {{ Number(item.amount) >= 0 ? "+" : "" }}{{ item.amount }} ر.س
            </span>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <span
              class="status_pill"
              :class="
                item.status === 'completed' || item.status === 1
                  ? 'completed'
                  : 'pending'
              "
            >
              {{ item.status || "مكتمل" }}
            </span>
          </template>

          <template v-slot:[`item.type`]="{ item }">
            <span class="type_pill">
              {{ item.type || "-" }}
            </span>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            <span class="date_text">
              <i class="fal fa-calendar-alt me-1 opacity-75"></i>
              {{ item.created_at || "-" }}
            </span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="_actions">
              <v-btn
                icon
                small
                color="purple"
                @click="showItem(item)"
                v-if="
                  item.model_type == 'App\\Models\\Delivery' ||
                  item.model_type == 'App\\Models\\Package'
                "
                title="عرض التفاصيل"
              >
                <v-icon small>fal fa-eye</v-icon>
              </v-btn>
              <span class="text-muted" v-else>-</span>
            </div>
          </template>
        </v-data-table>

        <!-- PAGINATION -->
        <div
          class="pagination_container text-center py-4 px-4 d-flex justify-content-end"
          v-if="paginations.last_page > 1"
        >
          <v-pagination
            v-model.number="paginations.current_page"
            :length="paginations.last_page"
            :total-visible="5"
            @input="fetchData($event)"
            color="purple darken-2"
          ></v-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  props: ["userInfo"],

  data() {
    return {
      transactions: null,
      data: {
        loading: false,
      },
      // ========== pagination
      rows: [],
      search: null,
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 20,
      },
    };
  },

  computed: {
    ...mapGetters({
      lang: "lang_module/lang",
    }),

    headers() {
      if (this.lang == "ar") {
        return [
          {
            text: "#",
            align: "center",
            value: "index",
            sortable: true,
            width: "80",
          },
          {
            text: "العنوان",
            align: "center",
            value: "title_trans",
            sortable: false,
          },
          {
            text: "القيمة",
            align: "center",
            value: "amount",
            sortable: false,
          },
          {
            text: "الحالة",
            align: "center",
            value: "status",
            sortable: false,
          },
          {
            text: "النوع",
            align: "center",
            value: "type",
            sortable: false,
          },
          {
            text: "تاريخ الإنشاء",
            align: "center",
            value: "created_at",
            sortable: false,
          },
          {
            text: "التحكم",
            value: "actions",
            sortable: false,
            align: "center",
          },
        ];
      } else {
        return [
          {
            text: "#",
            align: "center",
            value: "index",
            sortable: true,
          },
          {
            text: "Title",
            align: "center",
            value: "title_trans",
            sortable: false,
          },
          {
            text: "Amount",
            align: "center",
            value: "amount",
            sortable: false,
          },
          {
            text: "Status",
            align: "center",
            value: "status",
            sortable: false,
          },
          {
            text: "Actions",
            value: "actions",
            sortable: false,
            align: "center",
          },
        ];
      }
    },
  },
  methods: {
    getData() {
      this.data.loading = true;
      this.$axios({
        method: "GET",
        url: `transactions/${this.userInfo.id}`,
        params: {
          page: this.paginations.current_page,
        },
      })
        .then((res) => {
          this.paginations.last_page = res.data.meta
            ? res.data.meta.last_page
            : 1;
          this.paginations.items_per_page = res.data.meta
            ? res.data.meta.per_page
            : 20;
          this.transactions = res.data.data || [];

          this.data.loading = false;
        })
        .catch((err) => {
          this.transactions = [];
          this.$iziToast.error({
            timeout: 2000,
            message:
              err.response?.data?.message || "حدث خطأ أثناء جلب البيانات",
            position: "bottomRight",
          });
          this.data.loading = false;
        });
    },
    fetchData(e) {
      this.$router.replace({ query: { page: e } });
      this.getData();
    },
    showItem(item) {
      if (item.model_type == "App\\Models\\Delivery") {
        this.$router.push({ path: "/delivery_orders/show/" + item.order_id });
      } else if (item.model_type == "App\\Models\\Package") {
        this.$router.push({ path: "/package_order/show/" + item.order_id });
      }
    },
  },
  created() {
    if (this.userInfo && this.userInfo?.id) {
      this.getData();
    }
  },
};
</script>

<style lang="scss" scoped>
.wallet_data_page {
  /* HERO SUMMARY CARD */
  .wallet_hero_card {
    position: relative;
    border-radius: 24px;
    background: linear-gradient(
      135deg,
      #5b2857 0%,
      var(--mainColor-2, #783d73) 60%,
      #9c4b96 100%
    );
    box-shadow: 0 15px 35px -5px rgba(120, 61, 115, 0.3);
    overflow: hidden;
    color: #ffffff;
    padding: 30px 32px;

    .hero_ambient_glow {
      position: absolute;
      top: -50px;
      right: -50px;
      width: 200px;
      height: 200px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.15);
      filter: blur(40px);
      pointer-events: none;
    }

    .card_inner {
      position: relative;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 20px;
      flex-wrap: wrap;

      .info_col {
        .badge_pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: 50px;
          background: rgba(255, 255, 255, 0.18);
          backdrop-filter: blur(10px);
          font-size: 0.8rem;
          font-weight: 600;
          margin-bottom: 12px;
        }

        .hero_title {
          font-size: 1rem;
          font-weight: 500;
          opacity: 0.9;
          margin-bottom: 8px;
        }

        .balance_amount_row {
          display: flex;
          align-items: baseline;
          gap: 10px;

          .currency_sym {
            font-size: 1.25rem;
            font-weight: 700;
            opacity: 0.85;
          }

          .amount_num {
            font-size: 2.5rem;
            font-weight: 800;
            line-height: 1;
            letter-spacing: -0.5px;
          }
        }
      }

      .wallet_icon_col {
        .glowing_icon_box {
          width: 80px;
          height: 80px;
          border-radius: 24px;
          background: rgba(255, 255, 255, 0.18);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.2rem;
          color: #ffffff;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        }
      }
    }
  }

  /* CONTENT CARD & TABLE */
  .wallet_content_card {
    background: #ffffff;
    border-radius: 24px;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.04);
    overflow: hidden;

    .card_header_bar {
      border-bottom: 1px solid #f1f5f9;

      .header_title {
        display: flex;
        align-items: center;
        gap: 10px;

        i {
          font-size: 1.2rem;
          color: var(--mainColor-2, #783d73);
        }
      }

      .count_badge {
        font-size: 0.8rem;
        font-weight: 700;
        padding: 4px 12px;
        border-radius: 50px;
        background: rgba(120, 61, 115, 0.1);
        color: var(--mainColor-2, #783d73);
      }
    }
  }

  /* EMPTY STATE CONTAINER */
  .empty_state_container {
    padding: 60px 20px;

    .empty_icon_wrapper {
      position: relative;
      width: 90px;
      height: 90px;

      .empty_icon_box {
        width: 90px;
        height: 90px;
        border-radius: 26px;
        background: linear-gradient(
          135deg,
          rgba(120, 61, 115, 0.1) 0%,
          rgba(120, 61, 115, 0.04) 100%
        );
        border: 2px dashed rgba(120, 61, 115, 0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 2.4rem;
        color: var(--mainColor-2, #783d73);
        position: relative;
        z-index: 2;
      }

      .pulse_ring {
        position: absolute;
        inset: -8px;
        border-radius: 30px;
        background: rgba(120, 61, 115, 0.08);
        z-index: 1;
        animation: pulseRing 2.5s infinite ease-in-out;
      }
    }

    .empty_title {
      font-size: 1.25rem;
      color: #1e293b;
    }

    .empty_subtitle {
      font-size: 0.9rem;
      line-height: 1.6;
    }
  }

  /* TABLE SLOTS & BADGES */
  .index_badge {
    font-weight: 700;
    color: #94a3b8;
    font-size: 0.85rem;
  }

  .amount_badge {
    font-weight: 800;
    font-size: 0.95rem;
    padding: 4px 10px;
    border-radius: 10px;

    &.income {
      color: #10b981;
      background: rgba(16, 185, 129, 0.1);
    }

    &.expense {
      color: #ef4444;
      background: rgba(239, 68, 68, 0.1);
    }
  }

  .status_pill {
    font-size: 0.8rem;
    font-weight: 700;
    padding: 4px 12px;
    border-radius: 50px;

    &.completed {
      background: rgba(16, 185, 129, 0.12);
      color: #059669;
    }

    &.pending {
      background: rgba(245, 158, 11, 0.12);
      color: #d97706;
    }
  }

  .type_pill {
    font-size: 0.85rem;
    font-weight: 600;
    color: #475569;
  }

  .date_text {
    font-size: 0.825rem;
    color: #64748b;
  }
}

@keyframes pulseRing {
  0% {
    transform: scale(0.95);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.2;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.6;
  }
}
</style>
